import * as React from "react"

import { cn } from "../../../lib/utils"

/**
 * Classes the backdrop's host needs. `isolate` makes the host a stacking context, so the
 * backdrop's `-z-10` paints above the host's own background but below all in-flow content,
 * without any sibling needing a z-index.
 */
export const MEDIA_BACKDROP_HOST_CLASS = "relative isolate overflow-hidden"

// `scale-110` pushes the blur's soft edge outside the host, where `overflow-hidden` clips it.
// Opacity is deliberately high: only the media's own overlay chips sit on the backdrop, never card
// text. Dark mode needs more to read against the near-black card.
const BACKDROP_CLASS =
  "pointer-events-none absolute inset-0 -z-10 size-full scale-110 object-cover opacity-75 blur-sm dark:opacity-80"

export interface MediaBackdropProps {
  src: string | null | undefined
  type?: "image" | "video"
  className?: string
}

/**
 * Decorative, faded and blurred copy of a creative that fills the letterbox around it.
 * Render it as the last child of the media box (a `MEDIA_BACKDROP_HOST_CLASS` container), never
 * behind card text: muted captions only clear 4.5:1 by ~0.2, so any tint under them fails AA.
 */
export function MediaBackdrop({ src, type = "image", className }: MediaBackdropProps) {
  // Track which source failed, not a flag: callers like carousels swap `src` without remounting,
  // and one broken slide must not hide the backdrop for the next.
  const [failedSrc, setFailedSrc] = React.useState<string | null>(null)

  if (!src || failedSrc === src) return null

  const backdropProps = {
    "aria-hidden": true,
    "data-slot": "media-backdrop",
    className: cn(BACKDROP_CLASS, className),
    onError: () => setFailedSrc(src),
  }

  return type === "video" ? (
    <video src={src} muted playsInline preload="metadata" tabIndex={-1} {...backdropProps} />
  ) : (
    <img src={src} alt="" loading="lazy" decoding="async" {...backdropProps} />
  )
}
